import { gaTrack } from '../../shared/track.js'
import React, { createContext, useContext, useEffect } from 'react'
import type { CurrencyDisplayType } from '../../types/props.js'

/**
 * Exposure tracking parameters
 */
export interface ExposureParams {
  componentType: string
  componentName: string
  componentTitle?: string
  componentDescription?: string
  navigation?: string
  pageGroup?: string
  position?: number
}

/**
 * Callback function for custom exposure handling
 */
export type OnExposureCallback = (params: ExposureParams) => void

interface AiuiContextType {
  /**
   * 全局埋点数据，至少应包含 pageGroup 字段用于区分页面，其他字段可根据需要添加
   */
  trackingData: Record<string, any>
  /**
   * 当前语言环境，默认为浏览器语言
   */
  locale?: string
  /**
   * 当前页面路径，用于埋点区分不同页面，默认为 window.location.pathname
   */
  pathname?: string
  /**
   * 商户自定义的店铺域名，用于埋点区分不同商户，默认为空字符串
   */
  storeDomain?: string
  /**
   * 用户是否登录
   */
  isLogin?: boolean

  /**
   * 用户是否为Plus会员
   */
  isPlusMember?: boolean
  /**
   * 货币代码，默认为USD
   */
  currencyCode?: string
  /**
   * 货币显示方式，默认为symbol（符号），可选值包括：'symbol'（符号，如$）、'code'（货币代码，如USD）和'name'（货币名称，如美元）。
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currencydisplay for more details
   */
  currencyDisplay?: CurrencyDisplayType
  /**
   * 文案配置，商户可通过此字段覆盖默认文案
   */
  copyWriting?: Record<string, string>
  /**
   * 自定义曝光回调。传入后会替代默认的 GA 埋点逻辑。
   *
   * @example
   * <AiuiProvider
   *   trackingData={{ pageGroup: 'Home Page' }}
   *   onExposure={(params) => {
   *     // 使用商户自己的埋点系统记录曝光事件
   *     ga.track('component_impression', params)
   *   }}
   * >
   *   {children}
   * </AiuiProvider>
   */
  onExposure?: OnExposureCallback
}

export const AiuiContext = createContext<AiuiContextType>({
  trackingData: {},
  locale: undefined,
  copyWriting: {},
})

export const useAiuiContext = () => {
  const context = useContext(AiuiContext)
  if (!context) {
    throw new Error('useAiui must be used in <AiuiProvider>')
  }
  return context
}

const AiuiProvider = ({
  children,
  ...rest
}: {
  children: React.ReactNode
} & AiuiContextType) => {
  useEffect(() => {
    const handleTrackClick = (e: any, el: any) => {
      // e?.stopPropagation?.()
      const out: any = {}
      if (!el || !el.attributes) return out
      for (const attr of Array.from(el.attributes)) {
        const name = (attr as any)?.name
        if (name?.startsWith('data-headless-')) {
          out[name] = el.getAttribute(name)
        }
      }
      const sku = out?.['data-headless-sku']
      const nameType = out?.['data-headless-type-name']?.split?.('#')
      const titleDesc = out?.['data-headless-title-desc-button']?.split?.('#')
      const navPosition = out?.['data-headless-nav-position']?.split?.('#')
      gaTrack({
        event: 'ga4Event',
        event_name: 'component_click',
        event_parameters: {
          page_group: rest?.trackingData?.pageGroup || 'Home Page',
          component_type: nameType?.[0] || '',
          component_name: nameType?.[1] || '',
          position: navPosition?.[1] || '',
          navigation: navPosition?.[0] || '',
          button_name: titleDesc?.[2] || '',
          SKU: sku || '',
          component_title: titleDesc?.[0] || '',
          component_description: titleDesc?.[1] || '',
        },
      })
    }
    const observer = new MutationObserver(() => {
      document.querySelectorAll('[data-headless-type-name]').forEach(el => {
        if (!el.hasAttribute('data-headless-track-bound')) {
          el.addEventListener('click', e => handleTrackClick(e, el))
          el.setAttribute('data-headless-track-bound', 'true') // 避免重复绑定
        }
      })
    })
    observer.observe(document.body, { childList: true, subtree: true })
    // 初始化一次现有节点
    document.querySelectorAll('[data-headless-type-name]').forEach(el => {
      if (!el.hasAttribute('data-headless-track-bound')) {
        el.addEventListener('click', e => handleTrackClick(e, el))
        el.setAttribute('data-headless-track-bound', 'true')
      }
    })
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <AiuiContext.Provider value={rest}>{children}</AiuiContext.Provider>
}

export default AiuiProvider
